import { timerOutput } from "../behavior-timers/protocol.js";
import { evaluateDateDifference } from "./date-operators.js";
import type { AnimationEasing } from "../layout/types.js";
import {
  extendObject,
  selectObjectProperties,
  setObjectProperty,
  objectEntries,
  mapObjectValues,
} from "./object-operators.js";
import type {
  AnimationDirection,
  FontFamilyValue,
  AnimationFillMode,
  FontWeightValue,
  TextAlignValue,
  TextDecorationStyleValue,
  TextTransformValue,
  WhiteSpaceValue,
  BindingExpression,
  UnaryOperator,
  BinaryOperator,
  TernaryOperator,
  BindingOperatorOptions,
} from "./types.js";
import { BehaviorGraphPayload } from "./runtime/types.js";
import {
  FONT_FAMILY_VALUES,
  TEXT_ALIGN_VALUES,
  TEXT_DECORATION_LINE_VALUES,
  TEXT_DECORATION_STYLE_VALUES,
  TEXT_TRANSFORM_VALUES,
  WHITE_SPACE_VALUES,
} from "./types.js";
import { resolvePath } from "../layout/resolution.js";
import { getLanguage } from "../language/dico.js";
import {
  coerceToBoolean,
  toBorderConfig,
  toBorderRadiusConfig,
  toObjectValue,
} from "./runtime/helpers.js";
import { toListValue } from "./runtime/helpers.js";

/** Canonical CSS `animation-direction` keywords supported by behavior nodes. */
export const ANIMATION_DIRECTION_VALUES: readonly AnimationDirection[] = [
  "normal",
  "reverse",
  "alternate",
  "alternate-reverse",
];

/**
 * Normalizes a raw port/runtime value to a supported {@link AnimationDirection}.
 * Unknown values fall back to `"normal"`.
 *
 * @param raw - User input, literal, or wired value.
 * @returns A valid animation-direction keyword.
 */
export function normalizeAnimationDirectionValue(
  raw: unknown,
): AnimationDirection {
  const s = raw == null ? "" : String(raw).trim();
  return (ANIMATION_DIRECTION_VALUES as readonly string[]).includes(s)
    ? (s as AnimationDirection)
    : "normal";
}

/** Canonical CSS `animation-fill-mode` keywords supported by behavior nodes. */
export const ANIMATION_FILL_MODE_VALUES: readonly AnimationFillMode[] = [
  "none",
  "forwards",
  "backwards",
  "both",
];

/**
 * Normalizes a raw value to a supported {@link AnimationFillMode}.
 * Unknown values fall back to `"forwards"`.
 *
 * @param raw - User input, literal, or wired value.
 * @returns A valid animation-fill-mode keyword.
 */
export function normalizeAnimationFillModeValue(
  raw: unknown,
): AnimationFillMode {
  const s = raw == null ? "" : String(raw).trim();
  return (ANIMATION_FILL_MODE_VALUES as readonly string[]).includes(s)
    ? (s as AnimationFillMode)
    : "forwards";
}

/**
 * Canonical CSS {@link https://developer.mozilla.org/en-US/docs/Web/CSS/easing-function | easing-function}
 * keywords aligned with {@link AnimationEasing} in layout types (editor animations).
 */
export const ANIMATION_EASING_VALUES: readonly AnimationEasing[] = [
  "linear",
  "ease",
  "ease-in",
  "ease-out",
  "ease-in-out",
];

/**
 * Normalizes a raw value to a supported {@link AnimationEasing}.
 * Unknown values fall back to `"ease"`.
 *
 * @param raw - User input, literal, or wired value.
 * @returns A valid timing-function keyword.
 */
export function normalizeAnimationEasingValue(raw: unknown): AnimationEasing {
  const s = raw == null ? "" : String(raw).trim();
  return (ANIMATION_EASING_VALUES as readonly string[]).includes(s)
    ? (s as AnimationEasing)
    : "ease";
}

/**
 * Normalizes a raw value to a supported CSS `font-weight` keyword/value.
 * Unknown values fall back to `"normal"`.
 *
 * @param raw - User input, literal, or wired value.
 * @returns A valid font-weight value.
 */
export function normalizeFontWeightValue(raw: unknown): FontWeightValue {
  const s = raw == null ? "" : String(raw).trim().toLowerCase();
  if (s === "thin" || s === "100") return "100";
  if (s === "extra light" || s === "extralight" || s === "200") return "200";
  if (s === "light" || s === "300") return "300";
  if (s === "regular" || s === "normal" || s === "400") return "400";
  if (s === "medium" || s === "500") return "500";
  if (s === "semi bold" || s === "semibold" || s === "600") return "600";
  if (s === "bold" || s === "700") return "700";
  if (s === "extra bold" || s === "extrabold" || s === "800") return "800";
  if (s === "black" || s === "900") return "900";
  return "400";
}

/**
 * Normalizes a raw value to a `font-family` family-name.
 * Built-in families are canonicalized from {@link FONT_FAMILY_VALUES}.
 * User-uploaded custom families are preserved as-is (trimmed, quotes stripped).
 * Empty values fall back to the first built-in family.
 *
 * @param raw - User input, literal, or wired value.
 * @returns A font-family string safe to apply in layout styles.
 */
export function normalizeFontFamilyValue(raw: unknown): FontFamilyValue {
  const candidate = String(raw ?? "")
    .trim()
    .replace(/^['"]|['"]$/g, "");
  if (!candidate) {
    return FONT_FAMILY_VALUES[0];
  }
  const match = FONT_FAMILY_VALUES.find(
    (family) => family.toLowerCase() === candidate.toLowerCase(),
  );
  return (match ?? candidate) as FontFamilyValue;
}

/**
 * Returns whether `raw` matches a built-in {@link FONT_FAMILY_VALUES} entry
 * (case-insensitive). Custom / arbitrary strings are not treated as font families.
 *
 * @param raw - Candidate font family name.
 */
export function isKnownFontFamilyValue(raw: unknown): boolean {
  const candidate = String(raw ?? "")
    .trim()
    .replace(/^['"]|['"]$/g, "");
  if (!candidate) {
    return false;
  }
  return FONT_FAMILY_VALUES.some(
    (family) => family.toLowerCase() === candidate.toLowerCase(),
  );
}

/**
 * Normalizes a raw value to a supported CSS `text-align` keyword.
 * Unknown values fall back to `"left"`.
 *
 * @param raw - User input, literal, or wired value.
 * @returns A valid text-align value.
 */
export function normalizeTextAlignValue(raw: unknown): TextAlignValue {
  const candidate = String(raw ?? "")
    .trim()
    .toLowerCase();
  return (TEXT_ALIGN_VALUES as readonly string[]).includes(candidate)
    ? (candidate as TextAlignValue)
    : "left";
}

/**
 * Normalizes a value to a supported CSS `text-transform` keyword.
 * Unknown values fall back to `"none"`.
 *
 * @param raw - User input, literal, or wired value.
 * @returns A valid text-transform value.
 */
export function normalizeTextTransformValue(raw: unknown): TextTransformValue {
  const candidate = String(raw ?? "")
    .trim()
    .toLowerCase();
  return (TEXT_TRANSFORM_VALUES as readonly string[]).includes(candidate)
    ? (candidate as TextTransformValue)
    : "none";
}

/**
 * Normalizes a value to a supported CSS `white-space` keyword.
 * Unknown values fall back to `"normal"`.
 *
 * @param raw - User input, literal, or wired value.
 * @returns A valid white-space value.
 */
export function normalizeWhiteSpaceValue(raw: unknown): WhiteSpaceValue {
  if (raw == null) return "normal";
  const trimmed = String(raw).trim();
  if ((WHITE_SPACE_VALUES as readonly string[]).includes(trimmed)) {
    return trimmed as WhiteSpaceValue;
  }
  const lower = trimmed.toLowerCase();
  if (lower === "preserve nowrap") {
    return "preserve nowrap";
  }
  if ((WHITE_SPACE_VALUES as readonly string[]).includes(lower)) {
    return lower as WhiteSpaceValue;
  }
  return "normal";
}

const TEXT_DECORATION_LINE_SELECTABLE = TEXT_DECORATION_LINE_VALUES.filter(
  (value) => value !== "none",
);

/**
 * Parses a stored `text-decoration-line` value into selected keywords (excludes `none`).
 */
export function parseTextDecorationLineSelection(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw
      .map((entry) => String(entry).trim().toLowerCase())
      .filter((entry) =>
        (TEXT_DECORATION_LINE_SELECTABLE as readonly string[]).includes(entry),
      );
  }
  const trimmed = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (!trimmed || trimmed === "none") return [];
  return trimmed
    .split(/\s+/)
    .filter((entry) =>
      (TEXT_DECORATION_LINE_SELECTABLE as readonly string[]).includes(entry),
    );
}

/**
 * Formats selected decoration lines for storage and CSS (`none` when empty).
 */
export function formatTextDecorationLineSelection(
  lines: readonly string[],
): string {
  const unique = [
    ...new Set(
      lines
        .map((entry) => entry.trim().toLowerCase())
        .filter((entry) =>
          (TEXT_DECORATION_LINE_SELECTABLE as readonly string[]).includes(
            entry,
          ),
        ),
    ),
  ];
  if (unique.length === 0) return "none";
  return unique.join(" ");
}

/**
 * Normalizes a raw value to a supported CSS `text-decoration-line` string.
 */
export function normalizeTextDecorationLineValue(raw: unknown): string {
  return formatTextDecorationLineSelection(
    parseTextDecorationLineSelection(raw),
  );
}

/**
 * Normalizes a raw value to a supported CSS `text-decoration-style` keyword.
 */
export function normalizeTextDecorationStyleValue(
  raw: unknown,
): TextDecorationStyleValue {
  const candidate = String(raw ?? "")
    .trim()
    .toLowerCase();
  return (TEXT_DECORATION_STYLE_VALUES as readonly string[]).includes(candidate)
    ? (candidate as TextDecorationStyleValue)
    : "solid";
}

/**
 * Normalizes a decoration thickness in pixels (non-negative).
 */
export function normalizeTextDecorationThicknessValue(raw: unknown): number {
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, parsed);
}

export const getValueTypeColor = (valueType: string) => {
  switch (valueType) {
    case "boolean":
      return "#d64545";
    case "number":
      //green
      return "#78be21";
    case "string":
      return "#ee7ba3";
    case "list":
      return "#f43f5e";
    case "object":
      return "#8b5cf6";
    case "event":
      return "#d64545";
    case "control":
      return "#3b82f6";
    case "action":
      return "#0d9488";
    case "location":
      return "#f59e0b";
    case "transform":
      return "#7c3aed";
    case "date":
      return "#06b6d4";
    case "color":
      return "#f97316";
    case "gradient":
      return "#ec4899";
    case "filter":
      return "#7c3aed";
    case "boxShadow":
      return "#0284c7";
    case "textShadow":
      return "#0ea5a4";
    case "fontWeight":
      return "#8b5cf6";
    case "fontFamily":
      return "#2563eb";
    case "textAlign":
      return "#0f766e";
    case "textTransform":
      return "#047857";
    case "whiteSpace":
      return "#0d9488";
    case "textDecorationLine":
      return "#be123c";
    case "textDecorationColor":
      return "#ea580c";
    case "textDecorationStyle":
      return "#c026d3";
    case "textDecorationThickness":
      return "#65a30d";
    case "border":
      return "#10b981";
    case "borderRadius":
      return "#f59e0b";
    case "spacing":
      return "#0ea5e9";
    case "animationDirection":
      return "#6366f1";
    case "animationFill":
      return "#a855f7";
    case "animationEasing":
      return "#14b8a6";
    case "node":
      return "#475569";
    case "layoutListTarget":
      return "#334155";
    case "flow":
      return "#111111";
    case "any":
      return "green";
    default:
      return "#9e9e9e";
  }
};

export const sanitizeBehaviorGraph = (behaviorGraph: BehaviorGraphPayload) => {
  const { nodes, connections } = behaviorGraph;
  const sanitizedConnections = connections.filter((connection) => {
    const fromNode = nodes.find((node) => node.id === connection.fromNodeId);
    const toNode = nodes.find((node) => node.id === connection.toNodeId);
    if (!fromNode || !toNode) return false;
    return true;
  });
  return {
    nodes,
    connections: sanitizedConnections,
  };
};

/**
 * Checks whether a string is a valid CSS color value.
 *
 * In browser environments, this relies on the CSS parser (`CSS.supports` or
 * `HTMLElement.style.color`) so it supports modern syntaxes.
 * In non-browser environments, it falls back to a conservative parser that
 * accepts the most common CSS color notations.
 *
 * @param value - The candidate color string.
 * @returns True when the input is recognized as a valid CSS color.
 */
export const isColorString = (value: string): boolean => {
  const candidate = value.trim();
  if (!candidate) {
    return false;
  }

  if (typeof CSS !== "undefined" && typeof CSS.supports === "function") {
    return CSS.supports("color", candidate);
  }

  if (typeof document !== "undefined") {
    const probe = document.createElement("span");
    probe.style.color = "";
    probe.style.color = candidate;
    return probe.style.color !== "";
  }

  const hexColorRegex = /^#(?:[\da-f]{3}|[\da-f]{4}|[\da-f]{6}|[\da-f]{8})$/i;
  const rgbColorRegex =
    /^rgba?\(\s*\d{1,3}%?\s*,\s*\d{1,3}%?\s*,\s*\d{1,3}%?(?:\s*,\s*(?:\d*\.?\d+%?))?\s*\)$/i;
  const hslColorRegex =
    /^hsla?\(\s*-?\d*\.?\d+(?:deg|grad|rad|turn)?\s*,\s*\d{1,3}%\s*,\s*\d{1,3}%(?:\s*,\s*(?:\d*\.?\d+%?))?\s*\)$/i;
  const advancedFunctionColorRegex =
    /^(?:rgb|rgba|hsl|hsla|hwb|lab|lch|oklab|oklch|color|light-dark)\(.+\)$/i;
  const keywordColorRegex =
    /^(?:transparent|currentcolor|inherit|initial|unset|revert|revert-layer|[a-z]+)$/i;

  return (
    hexColorRegex.test(candidate) ||
    rgbColorRegex.test(candidate) ||
    hslColorRegex.test(candidate) ||
    advancedFunctionColorRegex.test(candidate) ||
    keywordColorRegex.test(candidate)
  );
};

const isCssPropertyValueSupported = (
  property: "filter" | "box-shadow" | "text-shadow" | "background-image",
  value: string,
): boolean => {
  const candidate = value.trim();
  if (!candidate) {
    return false;
  }

  if (typeof CSS !== "undefined" && typeof CSS.supports === "function") {
    return CSS.supports(property, candidate);
  }

  if (typeof document !== "undefined") {
    const probe = document.createElement("span");
    probe.style.setProperty(property, "");
    probe.style.setProperty(property, candidate);
    return probe.style.getPropertyValue(property) !== "";
  }

  return candidate === "none" || candidate.length > 0;
};

export const isFilterString = (value: string): boolean =>
  isCssPropertyValueSupported("filter", value);

export const isBoxShadowString = (value: string): boolean =>
  isCssPropertyValueSupported("box-shadow", value);

export const isTextShadowString = (value: string): boolean =>
  isCssPropertyValueSupported("text-shadow", value);

export const isGradientString = (value: string): boolean => {
  const candidate = value.trim();
  if (!candidate) {
    return false;
  }
  if (!/^(?:repeating-)?(?:linear|radial|conic)-gradient\(/i.test(candidate)) {
    return false;
  }
  return isCssPropertyValueSupported("background-image", candidate);
};

/**
 * Checks whether a string is a valid date-like value.
 *
 * Accepted formats:
 * - ISO datetime / date (`2026-04-17`, `2026-04-17T10:30:00Z`)
 * - French slash date (`17/04/2026`)
 *
 * The function validates both syntax and calendar correctness.
 *
 * @param value - The candidate date string.
 * @returns True when the input is a valid date string.
 */
export const isDateString = (value: string): boolean => {
  const candidate = value.trim();
  if (!candidate) {
    return false;
  }

  const isoDateRegex = /^\d{4}-\d{2}-\d{2}(?:[T\s].+)?$/;
  if (isoDateRegex.test(candidate)) {
    const dateOnlyMatch = candidate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, yearRaw, monthRaw, dayRaw] = dateOnlyMatch;
      const year = Number(yearRaw);
      const month = Number(monthRaw);
      const day = Number(dayRaw);
      const parsed = new Date(Date.UTC(year, month - 1, day));
      return (
        !Number.isNaN(parsed.getTime()) &&
        parsed.getUTCFullYear() === year &&
        parsed.getUTCMonth() === month - 1 &&
        parsed.getUTCDate() === day
      );
    }
    const parsed = new Date(candidate);
    return !Number.isNaN(parsed.getTime());
  }

  const frDateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const frMatch = candidate.match(frDateRegex);
  if (frMatch) {
    const [, dayRaw, monthRaw, yearRaw] = frMatch;
    const day = Number(dayRaw);
    const month = Number(monthRaw);
    const year = Number(yearRaw);
    const parsed = new Date(Date.UTC(year, month - 1, day));
    return (
      !Number.isNaN(parsed.getTime()) &&
      parsed.getUTCFullYear() === year &&
      parsed.getUTCMonth() === month - 1 &&
      parsed.getUTCDate() === day
    );
  }

  return false;
};

const HEX_COLOR_REGEX = /^#(?:[\da-f]{6}|[\da-f]{3})$/i;
const HEX_WITH_ALPHA_REGEX = /^#(?:[\da-f]{4}|[\da-f]{8})$/i;

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

const toHexByte = (n: number): string =>
  clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0");

/**
 * Parses any CSS-supported color string (named keyword like `red` / `blue`,
 * functional notation like `rgb(...)` / `hsl(...)` / `oklch(...)`, hex, etc.)
 * into an `{ r, g, b, a }` tuple where `r/g/b` ∈ [0,255] and `a` ∈ [0,1].
 *
 * Uses a canvas 2D context as the oracle so any color the host browser knows
 * is recognised - including modern CSS color functions. Returns `null` when:
 * - the input is not a string,
 * - no DOM/canvas is available (SSR, Node), or
 * - the browser cannot parse the value.
 *
 * @param input - Raw color string to parse.
 * @returns Parsed RGBA tuple, or `null` if the input cannot be resolved.
 */
function parseCssColorToRgba(
  input: unknown,
): { r: number; g: number; b: number; a: number } | null {
  if (typeof input !== "string") return null;
  const candidate = input.trim();
  if (!candidate) return null;
  if (typeof document === "undefined") return null;

  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "rgba(0, 0, 0, 0)";
    ctx.fillStyle = candidate;
    const filled = String(ctx.fillStyle);

    if (filled.startsWith("#")) {
      const hex = filled.slice(1);
      if (hex.length === 6) {
        return {
          r: parseInt(hex.slice(0, 2), 16),
          g: parseInt(hex.slice(2, 4), 16),
          b: parseInt(hex.slice(4, 6), 16),
          a: 1,
        };
      }
      if (hex.length === 8) {
        return {
          r: parseInt(hex.slice(0, 2), 16),
          g: parseInt(hex.slice(2, 4), 16),
          b: parseInt(hex.slice(4, 6), 16),
          a: parseInt(hex.slice(6, 8), 16) / 255,
        };
      }
      return null;
    }

    const rgbaMatch = filled.match(
      /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/i,
    );
    if (rgbaMatch) {
      return {
        r: Number(rgbaMatch[1]),
        g: Number(rgbaMatch[2]),
        b: Number(rgbaMatch[3]),
        a: rgbaMatch[4] !== undefined ? Number(rgbaMatch[4]) : 1,
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function toColorPickerValue(value: unknown): string {
  if (typeof value === "string") {
    const normalized = value.trim();
    if (HEX_WITH_ALPHA_REGEX.test(normalized)) {
      if (normalized.length === 5) {
        const r = normalized[1]!;
        const g = normalized[2]!;
        const b = normalized[3]!;
        return `#${r}${r}${g}${g}${b}${b}`;
      }
      return normalized.slice(0, 7);
    }
    if (HEX_COLOR_REGEX.test(normalized)) {
      if (normalized.length === 4) {
        const r = normalized[1]!;
        const g = normalized[2]!;
        const b = normalized[3]!;
        return `#${r}${r}${g}${g}${b}${b}`;
      }
      return normalized;
    }
    // Non-hex string: try to parse via the browser (named CSS colors like
    // `red`/`blue`/`green`, `rgb(...)`, `hsl(...)`, modern color functions…)
    const parsed = parseCssColorToRgba(normalized);
    if (parsed) {
      return `#${toHexByte(parsed.r)}${toHexByte(parsed.g)}${toHexByte(parsed.b)}`;
    }
    return "#000000";
  }
  return "#000000";
}

/**
 * Extracts the opacity percentage (0–100) from a CSS color string.
 *
 * When the value is missing (`null` / `undefined`), not a string, or blank,
 * defaults to **100** (fully opaque) so ColorPicker and callers start from a
 * usable opacity instead of an invisible color.
 *
 * @param value - Raw color value (hex, rgb/a, hsl/a, named CSS color, etc.).
 * @returns Opacity as an integer percentage in `[0, 100]`.
 */
export function toColorAlpha(value: unknown): number {
  if (typeof value !== "string") {
    return 100;
  }
  const normalized = value.trim();
  if (!normalized) {
    return 100;
  }

  if (HEX_WITH_ALPHA_REGEX.test(normalized)) {
    if (normalized.length === 5) {
      const a = normalized[4]!;
      const alphaHex = `${a}${a}`;
      return Math.round((parseInt(alphaHex, 16) / 255) * 100);
    }
    return Math.round((parseInt(normalized.slice(7, 9), 16) / 255) * 100);
  }

  const rgbaMatch = normalized.match(
    /^rgba?\(\s*[\d.]+%?[\s,]+[\d.]+%?[\s,]+[\d.]+%?(?:\s*(?:,|\/)\s*([\d.]+%?))?\s*\)$/i,
  );
  if (rgbaMatch?.[1]) {
    const alphaToken = rgbaMatch[1];
    if (alphaToken.endsWith("%")) {
      return clamp(Math.round(Number(alphaToken.slice(0, -1))), 0, 100);
    }
    return clamp(Math.round(Number(alphaToken) * 100), 0, 100);
  }

  const hslaMatch = normalized.match(
    /^hsla?\(\s*[-\d.]+(?:deg|rad|grad|turn)?[\s,]+[\d.]+%[\s,]+[\d.]+%(?:\s*(?:,|\/)\s*([\d.]+%?))?\s*\)$/i,
  );
  if (hslaMatch?.[1]) {
    const alphaToken = hslaMatch[1];
    if (alphaToken.endsWith("%")) {
      return clamp(Math.round(Number(alphaToken.slice(0, -1))), 0, 100);
    }
    return clamp(Math.round(Number(alphaToken) * 100), 0, 100);
  }

  const colorMixAlphaMatch = normalized.match(
    /^color-mix\(\s*in\s+srgb\s*,\s*transparent\s+([\d.]+)%\s*,/i,
  );
  if (colorMixAlphaMatch?.[1]) {
    return clamp(100 - Math.round(Number(colorMixAlphaMatch[1])), 0, 100);
  }

  // Generic fallback: ask the browser to parse the value (covers `transparent`
  // keyword, named colors with alpha via color-mix, modern CSS color
  // functions like `oklch()`, etc.).
  const parsed = parseCssColorToRgba(normalized);
  if (parsed) {
    return clamp(Math.round(parsed.a * 100), 0, 100);
  }

  return 100;
}

export function composeHexColorWithAlpha(
  hex: string,
  alphaPercent: number,
): string {
  const normalizedHex = toColorPickerValue(hex);
  const alpha = clamp(alphaPercent, 0, 100);
  if (alpha >= 100) {
    return normalizedHex;
  }
  const alphaHex = Math.round((alpha / 100) * 255)
    .toString(16)
    .padStart(2, "0");
  return `${normalizedHex}${alphaHex}`;
}

export const normalizeColorString = (raw: unknown): string =>
  String(raw ?? "")
    .trim()
    .toLowerCase();

export const toColorMixRatio = (raw: unknown): number => {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 50;
  return clamp(n, 0, 100);
};

/**
 * Loose equality for list find predicates (number, boolean, ISO-ish dates, string).
 */
export function listFindValuesMatch(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (a == null && b == null) return true;
  if (typeof a === "boolean" || typeof b === "boolean") {
    return Boolean(a) === Boolean(b);
  }
  if (typeof a === "number" || typeof b === "number") {
    const na = Number(a);
    const nb = Number(b);
    return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
  }
  const sa = typeof a === "string" ? a : String(a ?? "");
  const sb = typeof b === "string" ? b : String(b ?? "");
  if (isDateString(sa) && isDateString(sb)) {
    return new Date(sa).getTime() === new Date(sb).getTime();
  }
  return sa === sb;
}

/**
 * Returns the first list element whose field (or whole element if path empty) matches `needle`.
 */
/** Evaluate each item's keys once, then compare the cached keys in order. */
export function evaluateListSortBy(
  source: unknown,
  keysForItem: (item: unknown, index: number) => unknown,
): unknown[] {
  const decorated = toListValue(source).map((item, index) => {
    const value = keysForItem(item, index);
    return { item, index, keys: Array.isArray(value) ? value : [value] };
  });
  const missing = (value: unknown) =>
    value == null || (typeof value === "number" && !Number.isFinite(value));
  decorated.sort((a, b) => {
    for (
      let index = 0;
      index < Math.max(a.keys.length, b.keys.length);
      index++
    ) {
      const left = a.keys[index],
        right = b.keys[index];
      if (Object.is(left, right)) continue;
      if (missing(left) || missing(right)) {
        if (missing(left) && missing(right)) continue;
        return missing(left) ? 1 : -1;
      }
      const delta =
        typeof left === "number" && typeof right === "number"
          ? left - right
          : String(left).localeCompare(String(right), getLanguage(), {
              numeric: true,
            });
      if (delta) return delta;
    }
    return a.index - b.index;
  });
  return decorated.map(({ item }) => item);
}

export function evaluateListFind(
  list: unknown,
  needle: unknown,
  objectFieldPath?: string,
): unknown {
  const arr = toListValue(list);
  const path = objectFieldPath?.trim();
  return arr.find((item) => {
    const fieldValue =
      path && typeof item === "object" && item != null
        ? resolvePath(item, path)
        : item;
    return listFindValuesMatch(fieldValue, needle);
  });
}

/**
 * Same matching rules as {@link evaluateListFind} but returns the 0-based
 * index of the first match, or `-1` if none is found. Powers the
 * `outputIndex` port of the `find` operator.
 */
export function evaluateListFindIndex(
  list: unknown,
  needle: unknown,
  objectFieldPath?: string,
): number {
  const arr = toListValue(list);
  const path = objectFieldPath?.trim();
  return arr.findIndex((item) => {
    const fieldValue =
      path && typeof item === "object" && item != null
        ? resolvePath(item, path)
        : item;
    return listFindValuesMatch(fieldValue, needle);
  });
}

/**
 * Returns the first object element where `resolvePath(item, path)` matches `expected`.
 */
export function evaluateListFindByPath(
  list: unknown,
  pathRaw: unknown,
  expectedRaw: unknown,
): unknown {
  const arr = toListValue(list);
  const path = String(pathRaw ?? "").trim();
  if (!path) return undefined;
  return arr.find((item) => {
    if (item == null || typeof item !== "object") return false;
    const fieldValue = resolvePath(item, path);
    return listFindValuesMatch(fieldValue, expectedRaw);
  });
}

/**
 * Same matching rules as {@link evaluateListFindByPath} but returns the
 * 0-based index of the first match, or `-1` if none is found. Powers the
 * `outputIndex` port of the `findByPath` operator.
 */
export function evaluateListFindByPathIndex(
  list: unknown,
  pathRaw: unknown,
  expectedRaw: unknown,
): number {
  const arr = toListValue(list);
  const path = String(pathRaw ?? "").trim();
  if (!path) return -1;
  return arr.findIndex((item) => {
    if (item == null || typeof item !== "object") return false;
    const fieldValue = resolvePath(item, path);
    return listFindValuesMatch(fieldValue, expectedRaw);
  });
}

/** A group produced by the Blueprint `groupBy` list operator. */
export interface BlueprintListGroup {
  /** The value read from the configured grouping path. */
  key: unknown;
  /** Source items sharing the same key, in their original order. */
  items: unknown[];
}

/** Numeric statistics produced by the Blueprint `aggregateGroups` operator. */
export interface BlueprintAggregatedListGroup extends BlueprintListGroup {
  /** Number of finite numeric values included in the statistics. */
  count: number;
  /** Sum of the included values. */
  sum: number;
  /** Arithmetic mean, or zero when the group has no numeric value. */
  average: number;
  /** Smallest included value, or zero when the group has no numeric value. */
  min: number;
  /** Largest included value, or zero when the group has no numeric value. */
  max: number;
}

function toFiniteListNumbers(list: unknown): number[] {
  return toListValue(list).flatMap((value) => {
    if (
      value == null ||
      typeof value === "boolean" ||
      (typeof value === "string" && value.trim() === "")
    ) {
      return [];
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? [numeric] : [];
  });
}

/**
 * Returns the first requested items from a list without mutating it.
 *
 * Invalid or negative limits are normalized to zero.
 */
export function evaluateListTake(list: unknown, countRaw: unknown): unknown[] {
  const count = Number(countRaw);
  const normalizedCount = Number.isFinite(count)
    ? Math.max(0, Math.floor(count))
    : 0;
  return toListValue(list).slice(0, normalizedCount);
}

/** Returns the sum of every finite numeric value in a list. */
export function evaluateListSum(list: unknown): number {
  return toFiniteListNumbers(list).reduce((sum, value) => sum + value, 0);
}

/** Returns the arithmetic mean of every finite numeric value in a list. */
export function evaluateListAverage(list: unknown): number {
  const values = toFiniteListNumbers(list);
  return values.length > 0
    ? values.reduce((sum, value) => sum + value, 0) / values.length
    : 0;
}

/** Returns the smallest finite numeric value in a list, or zero if empty. */
export function evaluateListMin(list: unknown): number {
  const values = toFiniteListNumbers(list);
  return values.length > 0 ? Math.min(...values) : 0;
}

/** Returns the largest finite numeric value in a list, or zero if empty. */
export function evaluateListMax(list: unknown): number {
  const values = toFiniteListNumbers(list);
  return values.length > 0 ? Math.max(...values) : 0;
}

/**
 * Groups object items by a dot-path while preserving source and key order.
 */
export function evaluateListGroupBy(
  list: unknown,
  pathRaw: unknown,
): BlueprintListGroup[] {
  const path = String(pathRaw ?? "").trim();
  if (!path) return [];

  const groups = new Map<unknown, unknown[]>();
  for (const item of toListValue(list)) {
    const key =
      item != null && typeof item === "object"
        ? resolvePath(item, path)
        : undefined;
    const items = groups.get(key);
    if (items) {
      items.push(item);
    } else {
      groups.set(key, [item]);
    }
  }

  return Array.from(groups, ([key, items]) => ({ key, items }));
}

/**
 * Computes numeric statistics for each `{ key, items }` group using a value
 * selector evaluated once per item.
 *
 * Values that are empty, boolean, non-numeric, or non-finite are ignored.
 */
export function evaluateListAggregateGroupsBy(
  list: unknown,
  selectValue: (
    item: unknown,
    itemIndex: number,
    group: BlueprintListGroup,
  ) => unknown,
): BlueprintAggregatedListGroup[] {
  return toListValue(list).flatMap((candidate) => {
    if (
      candidate == null ||
      typeof candidate !== "object" ||
      !Array.isArray((candidate as { items?: unknown }).items)
    ) {
      return [];
    }

    const group = candidate as BlueprintListGroup;
    const values = toFiniteListNumbers(
      group.items.map((item, itemIndex) => selectValue(item, itemIndex, group)),
    );
    const count = values.length;
    const sum = values.reduce((total, value) => total + value, 0);

    return [
      {
        key: group.key,
        items: group.items,
        count,
        sum,
        average: count > 0 ? sum / count : 0,
        min: count > 0 ? Math.min(...values) : 0,
        max: count > 0 ? Math.max(...values) : 0,
      },
    ];
  });
}

/**
 * Path-based convenience wrapper for direct evaluator calls.
 *
 * Runtime Blueprint bindings use {@link evaluateListAggregateGroupsBy} so the
 * value may be an arbitrary expression such as `points * totalkg`.
 */
export function evaluateListAggregateGroups(
  list: unknown,
  pathRaw: unknown,
): BlueprintAggregatedListGroup[] {
  const path = String(pathRaw ?? "").trim();
  if (!path) return [];
  return evaluateListAggregateGroupsBy(list, (item) =>
    item != null && typeof item === "object"
      ? resolvePath(item, path)
      : undefined,
  );
}

function evaluateTernaryBindingOperator(
  operator: TernaryOperator,
  input1: unknown,
  input2: unknown,
  input3: unknown,
  options?: BindingOperatorOptions,
): unknown {
  if (operator === "setProperty")
    return setObjectProperty(input1, input2, input3);
  switch (operator) {
    case "replace":
      return String(input1 ?? "").replace(
        String(input2 ?? ""),
        String(input3 ?? ""),
      );

    case "boolToSign":
      return coerceToBoolean(input1) ? Number(input2) : Number(input3);

    case "ifElse":
      return coerceToBoolean(input3) ? input1 : input2;

    case "mix": {
      const color1 = String(input1 ?? "").trim();
      const color2 = String(input2 ?? "").trim();

      if (!isColorString(color1) || !isColorString(color2)) {
        return "";
      }

      const ratio = toColorMixRatio(input3);
      const leftPercent = Math.round(100 - ratio);
      const rightPercent = Math.round(ratio);

      return `color-mix(in srgb, ${color1} ${leftPercent}%, ${color2} ${rightPercent}%)`;
    }

    case "findByPath":
      return options?.outputKind === "index"
        ? evaluateListFindByPathIndex(input1, input2, input3)
        : evaluateListFindByPath(input1, input2, input3);

    default:
      return undefined;
  }
}

function evaluateBinaryBindingOperator(
  operator: BinaryOperator,
  input1: unknown,
  input2: unknown,
  options?: BindingOperatorOptions,
): unknown {
  if (operator === "extendObject") return extendObject(input1, input2);
  if (operator === "pickObject") return selectObjectProperties(input1, input2);
  if (operator === "omitObject")
    return selectObjectProperties(input1, input2, true);
  switch (operator) {
    case "dateDifference":
      return evaluateDateDifference(
        input1,
        input2,
        options?.dateDifferencePart,
      );
    case "and":
      return coerceToBoolean(input1) && coerceToBoolean(input2);

    case "or":
      return coerceToBoolean(input1) || coerceToBoolean(input2);

    case "equals":
      if (options?.input1ValueType === "boolean")
        return coerceToBoolean(input1) === coerceToBoolean(input2);
      return input1 === input2 || Number(input1) === Number(input2);

    case "notEquals":
      if (options?.input1ValueType === "boolean")
        return coerceToBoolean(input1) !== coerceToBoolean(input2);
      return input1 !== input2 && Number(input1) !== Number(input2);

    case "greaterThan":
      return Number(input1) > Number(input2);

    case "lessThan":
      return Number(input1) < Number(input2);

    case "greaterThanOrEqual":
      return Number(input1) >= Number(input2);

    case "lessThanOrEqual":
      return Number(input1) <= Number(input2);

    case "contains":
      if (Array.isArray(input1)) {
        const serializedNeedle = String(input2 ?? "");

        return input1.some(
          (item) =>
            String(item) === serializedNeedle ||
            JSON.stringify(item) === serializedNeedle,
        );
      }

      return String(input1 ?? "").includes(String(input2 ?? ""));

    case "notContains":
      return !String(input1 ?? "").includes(String(input2 ?? ""));

    case "startsWith":
      return String(input1 ?? "").startsWith(String(input2 ?? ""));

    case "endsWith":
      return String(input1 ?? "").endsWith(String(input2 ?? ""));

    case "add":
      return Number(input1) + Number(input2);

    case "subtract":
      return Number(input1) - Number(input2);

    case "multiply":
      return Number(input1) * Number(input2);

    case "divide": {
      const divisor = Number(input2);

      return divisor === 0 ? 0 : Number(input1) / divisor;
    }

    case "modulo": {
      const mod = Number(input2);

      return mod === 0 ? 0 : Number(input1) % mod;
    }

    case "power":
      return Math.pow(Number(input1), Number(input2));

    case "round": {
      const value = Number(input1);
      const rawDecimals = Number(input2);
      if (!Number.isFinite(value)) {
        return Number.NaN;
      }
      const decimals = Number.isFinite(rawDecimals)
        ? Math.max(0, Math.min(20, Math.floor(rawDecimals)))
        : 0;
      const factor = 10 ** decimals;

      return Math.round(value * factor) / factor;
    }

    case "concat":
      return String(input1 ?? "") + String(input2 ?? "");

    case "chartAt": {
      const source = String(input1 ?? "");
      const index = Number(input2);
      if (!Number.isInteger(index) || index < 0 || index >= source.length) {
        return "";
      }
      return source.charAt(index);
    }

    case "compare":
      return String(input1 ?? "").localeCompare(
        String(input2 ?? ""),
        getLanguage(),
        {
          sensitivity: "base",
          numeric: true,
        },
      );

    case "join":
      return toListValue(input1).join(String(input2 ?? ","));

    case "push":
      return [...toListValue(input1), input2];

    case "remove": {
      const source = [...toListValue(input1)];
      const index = Number(input2);

      if (!Number.isInteger(index) || index < 0 || index >= source.length) {
        return source;
      }

      source.splice(index, 1);

      return source;
    }

    case "take":
      return evaluateListTake(input1, input2);

    case "get": {
      /**
       * Dual-purpose `get`:
       * - If `input2` is an integer -> treat `input1` as a list and return `input1[input2]`.
       * - Otherwise, treat `input2` as a (possibly dotted) field path and resolve it on `input1`.
       *
       * This enables both patterns:
       *   athletes[index]         // list get
       *   athlete["nom"]          // object field get
       *   athlete["catAge.nom"]  // nested field get
       */
      if (input1 == null) return undefined;

      const maybeIndex = typeof input2 === "number" ? input2 : Number(input2);
      const canUseIndex = Number.isInteger(maybeIndex) && maybeIndex >= 0;

      if (canUseIndex) {
        const source = toListValue(input1);
        if (maybeIndex >= source.length) return undefined;
        return source[maybeIndex];
      }

      // Field/path access mode.
      const path =
        typeof input2 === "string"
          ? input2.trim()
          : String(input2 ?? "").trim();
      if (!path) return undefined;

      return resolvePath(input1, path);
    }

    case "map": {
      const source = toListValue(input1);
      const path = String(input2 ?? "").trim();
      if (!path) {
        return source;
      }
      return source.map((item) => resolvePath(item, path));
    }

    case "groupBy":
      return evaluateListGroupBy(input1, input2);

    case "aggregateGroups":
      return evaluateListAggregateGroupsBy(input1, () => input2);

    case "find":
      // `outputKind` is set by `OperatorBehaviorNode` per-port at binding-
      // time: `"index"` for the `outputIndex` port, `"item"` (default) for
      // the `output` port.
      return options?.outputKind === "index"
        ? evaluateListFindIndex(
            input1,
            input2,
            options?.objectFieldPath ?? undefined,
          )
        : evaluateListFind(
            input1,
            input2,
            options?.objectFieldPath ?? undefined,
          );

    case "findByCondition": {
      // Nominal path: `resolveBindingExpression`'s binary case handles
      // `findByCondition` specially (per-item iteration with scoped data
      // injection). This branch is only reached if the binary evaluator is
      // called with already-resolved inputs (e.g. evaluator unit tests),
      // so we apply a degenerate fallback honouring `outputKind`:
      //   - truthy predicate ⇒ first element / index 0
      //   - falsy predicate  ⇒ undefined / -1
      const truthy = coerceToBoolean(input2);
      if (options?.outputKind === "index") {
        return truthy ? 0 : -1;
      }
      return truthy ? toListValue(input1)[0] : undefined;
    }

    case "filter":
      // Nominal path: `resolveBindingExpression` handles `filter` specially
      // (per-item iteration). This degenerate fallback is only reached when
      // the binary evaluator is called directly with resolved inputs:
      // truthy predicate ⇒ pass-through, falsy ⇒ empty list.
      return coerceToBoolean(input2) ? [...toListValue(input1)] : [];

    case "sortBy":
    case "sort":
      // Nominal path: `resolveBindingExpression` handles `sort` specially
      // (per-pair comparator iteration). This degenerate fallback is only
      // reached when the binary evaluator is called directly: return the
      // list unchanged (a single pre-resolved comparator value cannot drive
      // a per-pair sort).
      return [...toListValue(input1)];

    case "getByPath": {
      if (input1 == null || typeof input1 !== "object") {
        return "";
      }

      const path = String(input2 ?? "").trim();

      if (!path) {
        return "";
      }

      const resolved = resolvePath(input1, path);

      return resolved == null ? "" : String(resolved);
    }

    case "withAlpha": {
      const color = String(input1 ?? "").trim();

      if (!isColorString(color)) {
        return "";
      }

      const alpha = clamp(Number(input2), 0, 1);
      const opaquePercent = Math.round((1 - alpha) * 100);

      return `color-mix(in srgb, transparent ${opaquePercent}%, ${color})`;
    }

    case "isSame":
      return (
        new Date(String(input1)).getTime() ===
        new Date(String(input2)).getTime()
      );

    case "isAfter":
      return (
        new Date(String(input1)).getTime() > new Date(String(input2)).getTime()
      );

    case "isBefore":
      return (
        new Date(String(input1)).getTime() < new Date(String(input2)).getTime()
      );

    case "addDays":
      return new Date(
        new Date(String(input1)).getTime() +
          Number(input2) * 24 * 60 * 60 * 1000,
      );

    case "addMonths":
      return new Date(
        new Date(String(input1)).getTime() +
          Number(input2) * 30 * 24 * 60 * 60 * 1000,
      );

    case "addYears":
      return new Date(
        new Date(String(input1)).getTime() +
          Number(input2) * 365 * 24 * 60 * 60 * 1000,
      );

    case "addHours":
      return new Date(
        new Date(String(input1)).getTime() + Number(input2) * 60 * 60 * 1000,
      );

    case "addMinutes":
      return new Date(
        new Date(String(input1)).getTime() + Number(input2) * 60 * 1000,
      );

    case "addSeconds":
      return new Date(
        new Date(String(input1)).getTime() + Number(input2) * 1000,
      );

    default:
      return undefined;
  }
}

/** Whether an `object`-typed value is present (non-null plain object). */
export function evaluateObjectDefined(value: unknown): boolean {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

/** Whether an `object`-typed value is absent or an empty plain object `{}`. */
export function evaluateObjectEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value !== "object" || Array.isArray(value)) return true;
  return Object.keys(value as Record<string, unknown>).length === 0;
}

/**
 * Serializes an object-typed blueprint value to string via JSON (inverse of `castToObject`).
 */
export function objectValueToString(value: unknown): string {
  if (value == null) {
    return "";
  }
  if (typeof value !== "object") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
}

function evaluateUnaryBindingOperator(
  operator: UnaryOperator,
  value: unknown,
  options?: BindingOperatorOptions,
): unknown {
  if (operator === "objectKeys")
    return objectEntries(value).map((item) => item.key);
  if (operator === "objectValues")
    return objectEntries(value).map((item) => item.value);
  if (operator === "objectEntries") return objectEntries(value);
  const inputIsObject = options?.input1ValueType === "object";

  switch (operator) {
    case "defined":
      if (inputIsObject) {
        return evaluateObjectDefined(value);
      }
      return (
        value != null &&
        value !== "" &&
        !(typeof value === "number" && Number.isNaN(value))
      );

    case "empty":
      if (inputIsObject) {
        return evaluateObjectEmpty(value);
      }
      if (Array.isArray(value)) {
        return value.length === 0;
      }

      return (
        value == null ||
        value === "" ||
        (typeof value === "number" && Number.isNaN(value))
      );

    case "not":
      return !coerceToBoolean(value);

    case "length":
      return toListValue(value).length;

    case "isNotEmpty":
      return toListValue(value).length > 0;

    case "sum":
      return evaluateListSum(value);

    case "average":
      return evaluateListAverage(value);

    case "min":
      return evaluateListMin(value);

    case "max":
      return evaluateListMax(value);

    case "currentEntry":
    case "currentItem":
    case "comparator":
      // Not the nominal binding path. `OperatorBehaviorNode` emits a
      // `BindingPath` for `currentItem` (`iterationItem`) and for
      // `comparator` (`iterationItemA` / `iterationItemB` per output port),
      // which the recursive resolver fills from the scoped data injected
      // by `findByCondition` / `filter` / `sort`. This eager fallback is
      // only reached if a caller invokes the unary evaluator with an
      // already-resolved value (e.g. evaluator unit tests).
      return undefined;

    case "ceil":
      return Math.ceil(Number(value));
    case "factorial": {
      const n = Number(value);

      if (!Number.isFinite(n) || n < 0) {
        return 0;
      }

      let result = 1;

      for (let i = 2; i <= Math.floor(n); i++) {
        result *= i;
      }

      return result;
    }

    case "castToString":
      if (inputIsObject) {
        return objectValueToString(value);
      }
      return value instanceof Date
        ? value.toLocaleDateString(getLanguage())
        : String(value);

    case "castToNumber":
      return Number(value);

    case "castToBoolean":
      if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized === "true" || normalized === "1") return true;
        if (normalized === "false" || normalized === "0" || normalized === "")
          return false;
      }
      return Boolean(value);

    case "castToColor": {
      const candidate = String(value ?? "").trim();

      return isColorString(candidate) ? candidate : "#00000000";
    }

    case "castToGradient": {
      const candidate = String(value ?? "").trim();
      return isGradientString(candidate)
        ? candidate
        : "linear-gradient(90deg, #000000 0%, #ffffff 100%)";
    }

    case "castToFilter": {
      const candidate = String(value ?? "").trim();
      return isFilterString(candidate) ? candidate : "none";
    }

    case "castToBoxShadow": {
      const candidate = String(value ?? "").trim();
      return isBoxShadowString(candidate) ? candidate : "none";
    }

    case "castToTextShadow": {
      const candidate = String(value ?? "").trim();
      return isTextShadowString(candidate) ? candidate : "none";
    }

    case "castToFontFamily":
      return normalizeFontFamilyValue(value);

    case "castToFontWeight":
      return normalizeFontWeightValue(value);

    case "castToTextAlign":
      return normalizeTextAlignValue(value);

    case "castToTextTransform":
      return normalizeTextTransformValue(value);

    case "castToWhiteSpace":
      return normalizeWhiteSpaceValue(value);

    case "castToTextDecorationLine":
      return normalizeTextDecorationLineValue(value);

    case "castToTextDecorationStyle":
      return normalizeTextDecorationStyleValue(value);

    case "castToBorder":
      return toBorderConfig(value);

    case "castToBorderRadius":
      return toBorderRadiusConfig(value);

    case "castToObject":
      return toObjectValue(value);

    case "toUpperCase":
      return String(value ?? "").toUpperCase();

    case "toLowerCase":
      return String(value ?? "").toLowerCase();

    case "capitalize": {
      const str = String(value ?? "");

      return str.charAt(0).toUpperCase() + str.slice(1);
    }

    case "getDay": {
      const locale = getLanguage();
      const date = new Date(String(value));
      const format = options?.datePartTextFormat;

      if (!format || format === "numeric") {
        return date.getDay();
      }

      if (format === "narrow" || format === "short" || format === "long") {
        return date.toLocaleDateString(locale, {
          weekday: format,
        });
      }

      return date.toLocaleDateString(locale, {
        day: format,
      });
    }

    case "getMonth": {
      const locale = getLanguage();
      const date = new Date(String(value));
      const format = options?.datePartTextFormat;

      if (!format || format === "numeric") {
        return date.getMonth();
      }

      return date.toLocaleDateString(locale, {
        month: format,
      });
    }

    case "getYear":
      return new Date(String(value)).getFullYear();

    case "getHour":
      return new Date(String(value)).getHours();

    case "getMinute":
      return new Date(String(value)).getMinutes();

    case "getSecond":
      return new Date(String(value)).getSeconds();

    case "get":
      /**
       * Dans ton getOutput(binding), tu transformes déjà le "get"
       * en path complet :
       *
       * objectSource + "." + objectFieldPath
       *
       * Donc ici, le input est déjà la valeur résolue.
       */
      return value;

    default:
      return undefined;
  }
}

export function resolveBindingExpression(
  expression: BindingExpression | undefined,
  data: unknown,
): unknown {
  if (!expression) return undefined;

  switch (expression.kind) {
    case "binding":
      return resolvePath(data, expression.path);

    case "literal":
      return expression.value;

    case "unary":
      if (expression.operator === "readTimer")
        return timerOutput(
          (data as import("../layout/types.js").InjectedData)?.behaviorTimers,
          resolveBindingExpression(expression.input, data),
          expression.options?.timerOutputPort ?? "output",
        );
      {
        const input = resolveBindingExpression(expression.input, data);
        if (
          expression.operator === "currentItem" ||
          expression.operator === "currentEntry"
        ) {
          const scoped = data as
            | {
                iterationItem?: unknown;
                __iterationScopes?: Array<{ list: unknown; item: unknown }>;
              }
            | undefined;
          const scope = scoped?.__iterationScopes
            ?.slice()
            .reverse()
            .find((scope) => scope.list === input);
          const item = scope ? scope.item : scoped?.iterationItem;
          return expression.options?.objectFieldPath
            ? resolvePath(item, expression.options.objectFieldPath)
            : item;
        }
        return evaluateUnaryBindingOperator(
          expression.operator,
          input,
          expression.options,
        );
      }

    case "binary": {
      if (
        expression.operator === "mapItems" ||
        expression.operator === "mapValues"
      ) {
        const source = resolveBindingExpression(expression.input1, data);
        const base = data && typeof data === "object" ? data : {};
        const scopes =
          (
            base as {
              __iterationScopes?: Array<{ list: unknown; item: unknown }>;
            }
          ).__iterationScopes ?? [];
        const transform = (item: unknown, index: number) =>
          resolveBindingExpression(expression.input2, {
            ...base,
            iterationItem: item,
            iterationIndex: index,
            __iterationScopes: [...scopes, { list: source, item }],
          });
        return expression.operator === "mapItems"
          ? toListValue(source).map(transform)
          : mapObjectValues(source, (value, key, index) =>
              transform({ key, value }, index),
            );
      }
      // `aggregateGroups` evaluates its numeric expression once per item of
      // each `{ key, items }` group. `currentItem` bindings resolve through
      // the scoped `iterationItem` / `iterationIndex` values.
      if (expression.operator === "aggregateGroups") {
        const groups = resolveBindingExpression(expression.input1, data);
        const baseData =
          data && typeof data === "object"
            ? (data as Record<string, unknown>)
            : {};
        return evaluateListAggregateGroupsBy(groups, (item, itemIndex) =>
          resolveBindingExpression(expression.input2, {
            ...baseData,
            iterationItem: item,
            iterationIndex: itemIndex,
          }),
        );
      }

      // `findByCondition` / `filter` need to re-evaluate their predicate
      // (input2) once per list item with `iterationItem` / `iterationIndex`
      // injected into the resolution context, so the upstream `currentItem`
      // binding (which points to `iterationItem`) resolves to the iterated
      // item. Eager resolution of `input2` would only see the outer `data`.
      // The iteration scope keys are intentionally distinct from
      // `layoutListRowItem` / `rowIndex` (owned by `LayoutList`) to avoid
      // shadowing when the operators are used inside a `LayoutList` behavior.
      if (
        expression.operator === "findByCondition" ||
        expression.operator === "filter"
      ) {
        const list = toListValue(
          resolveBindingExpression(expression.input1, data),
        );
        const baseData =
          data && typeof data === "object"
            ? (data as Record<string, unknown>)
            : {};
        const matches: unknown[] = [];
        // For `findByCondition`, `outputKind === "index"` selects the
        // `outputIndex` port (returns the matched index, `-1` if no match).
        const wantsIndex =
          expression.operator === "findByCondition" &&
          expression.options?.outputKind === "index";
        for (let index = 0; index < list.length; index += 1) {
          const item = list[index];
          const scopedData: Record<string, unknown> = {
            ...baseData,
            iterationItem: item,
            iterationIndex: index,
          };
          const predicateResult = resolveBindingExpression(
            expression.input2,
            scopedData,
          );
          if (coerceToBoolean(predicateResult)) {
            if (expression.operator === "findByCondition") {
              return wantsIndex ? index : item;
            }
            matches.push(item);
          }
        }
        if (expression.operator === "filter") return matches;
        return wantsIndex ? -1 : undefined;
      }

      // `sort` evaluates a numeric comparator (input2) per pair with
      // `iterationItemA` / `iterationItemB` injected into the resolution
      // context, so the upstream `comparator` operator resolves to the
      // pair currently being compared. Stable tie-break on original index.
      if (expression.operator === "sortBy") {
        const list = resolveBindingExpression(expression.input1, data);
        const base = data && typeof data === "object" ? data : {};
        return evaluateListSortBy(list, (item, index) =>
          resolveBindingExpression(expression.input2, {
            ...base,
            iterationItem: item,
            iterationIndex: index,
            __iterationScopes: [
              ...((
                base as {
                  __iterationScopes?: { list: unknown; item: unknown }[];
                }
              ).__iterationScopes ?? []),
              { list, item },
            ],
          }),
        );
      }
      if (expression.operator === "sort") {
        const list = toListValue(
          resolveBindingExpression(expression.input1, data),
        );
        const baseData =
          data && typeof data === "object"
            ? (data as Record<string, unknown>)
            : {};
        const indices = list.map((_, index) => index);
        indices.sort((aIndex, bIndex) => {
          const scopedData: Record<string, unknown> = {
            ...baseData,
            iterationItemA: list[aIndex],
            iterationItemB: list[bIndex],
          };
          const result = resolveBindingExpression(
            expression.input2,
            scopedData,
          );
          const numeric = Number(result);
          if (!Number.isFinite(numeric) || numeric === 0) {
            return aIndex - bIndex;
          }
          return numeric;
        });
        return indices.map((index) => list[index]);
      }

      const input1 = resolveBindingExpression(expression.input1, data);
      const input2 = resolveBindingExpression(expression.input2, data);

      return evaluateBinaryBindingOperator(
        expression.operator,
        input1,
        input2,
        expression.options,
      );
    }

    case "ternary": {
      const input1 = resolveBindingExpression(expression.input1, data);
      const input2 = resolveBindingExpression(expression.input2, data);
      const input3 = resolveBindingExpression(expression.input3, data);

      return evaluateTernaryBindingOperator(
        expression.operator,
        input1,
        input2,
        input3,
        expression.options,
      );
    }

    default:
      return undefined;
  }
}
